import 'cally';

import { submitForm } from '../utils/form-submit';
import { withProductPicker } from '../utils/product-picker';

const MIN_VISIT_DAYS_AHEAD = 2;

// Local YYYY-MM-DD (toISOString would shift the date across timezones)
function formatDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Showroom visit form: pick a product, then a date and time.
 *
 * The calendar is cally's `<calendar-date>`, which brings its own keyboard and
 * screen reader support. `visitDate` is bound to its value both ways, so every
 * downstream step reads that state.
 */
export function showroomVisitForm({ availability = {}, timeSlots = [], ...pickerConfig } = {}) {
  return withProductPicker(pickerConfig, {
    timeSlots,
    bookedSlots: availability.bookedSlots ?? {},
    blockedDates: availability.blockedDates ?? [],
    closedWeekdays: availability.closedWeekdays ?? [],
    visitDate: '',
    visitTime: '',
    shownMonth: null,

    /** Times only make sense once a day has been chosen. */
    get canPickTime() {
      return this.visitDate !== '';
    },

    /** Slots an upcoming visit already requested on the chosen day. */
    isTimeBooked(time) {
      return (this.bookedSlots[this.visitDate] ?? []).includes(time);
    },

    /** A visit needs a date, and a time whenever slots are configured. */
    get isComplete() {
      return this.visitDate !== '' && (this.timeSlots.length === 0 || this.visitTime !== '');
    },

    /** cally's own header shows just the year, which reads as if the arrows page by year. */
    get calendarHeading() {
      return this.shownMonth?.toLocaleDateString(this.$refs.calendar.locale, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
    },

    /** Blocked dates, closed weekdays and fully booked days can't be picked. */
    isDayUnavailable(date, weekday) {
      return (
        this.blockedDates.includes(date) ||
        this.closedWeekdays.includes(weekday) ||
        (this.timeSlots.length > 0 &&
          this.timeSlots.every((time) => (this.bookedSlots[date] ?? []).includes(time)))
      );
    },

    init() {
      const today = formatDate(new Date());
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + MIN_VISIT_DAYS_AHEAD);

      // cally passes days as UTC midnight, so they're read back in UTC
      Object.assign(this.$refs.calendar, {
        min: formatDate(minDate),
        isDateDisallowed: (date) =>
          this.isDayUnavailable(date.toISOString().slice(0, 10), date.getUTCDay()),
        // Days before `min` can't be picked either; past ones are marked so the
        // CSS can leave them out, while the rest stay shown
        getDayParts: (date) => (date.toISOString().slice(0, 10) < today ? 'past' : ''),
      });

      // Afterwards cally reports every page change through `focusday`
      this.shownMonth = new Date(Date.UTC(minDate.getFullYear(), minDate.getMonth()));

      this.applyDeepLink();
    },

    /** A `?product_id=` link from a product page preselects that product. */
    applyDeepLink() {
      const productId = new URLSearchParams(window.location.search).get('product_id');

      if (!productId || !this.applyDeepLinkedProduct(Number(productId))) {
        return;
      }

      this.$nextTick(() => {
        this.$refs.picker
          ?.querySelector(`.product-choice[data-product-id="${productId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    /** Choosing a different product invalidates nothing else here. */
    onProductChange() {},

    selectDate(date) {
      this.visitDate = date;

      if (this.isTimeBooked(this.visitTime)) {
        this.visitTime = '';
      }
    },

    toggleTime(time) {
      this.visitTime = this.visitTime === time ? '' : time;
    },

    isTimeSelected(time) {
      return this.visitTime === time;
    },

    submit() {
      submitForm({
        form: this.$refs.form,
        url: window.js_localizations.showroom_submission_url,
        extraFields: { product_id: this.selectedProductId ?? '' },
        event: 'generate_lead',
        eventData: { form_name: 'showroom_visit_form', lead_type: 'showroom_visit' },
        onSuccess: () => {
          this.$refs.form.reset();
          this.visitDate = '';
          this.visitTime = '';
          this.selectProduct(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      });
    },
  });
}
